<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { DirectUpload } from 'activestorage';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'duplicate'].includes(value),
  },
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getDisparosInboxes'),
};

const MAX_MEDIA_ITEMS = 10;

const mediaItems = ref([]);

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  sendDelay: '00:27',
};

const state = reactive({ ...initialState });

const parseMMSS = value => {
  if (!value || typeof value !== 'string') return 0;
  const parts = value.split(':');
  if (parts.length !== 2) return parseInt(value, 10) || 0;
  return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
};

const formatSecondsToMMSS = seconds => {
  if (!seconds && seconds !== 0) return '00:03';
  const s = parseInt(seconds, 10) || 0;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const mediaTypeOptions = [
  { value: 'image', label: 'Imagem' },
  { value: 'video', label: 'Vídeo' },
  { value: 'audio', label: 'Áudio' },
  { value: 'document', label: 'Documento' },
];

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.DISPARO.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() =>
  v$.value.$invalid || mediaItems.value.some(item => item.isUploading)
);

const mediaTypeFromFile = file => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'document';
};

const addMediaItem = () => {
  if (mediaItems.value.length >= MAX_MEDIA_ITEMS) return;
  mediaItems.value.push({
    id: Date.now(),
    file: null,
    previewUrl: null,
    mediaUrl: '',
    mediaType: '',
    caption: '',
    isUploading: false,
  });
};

const removeMediaItem = id => {
  mediaItems.value = mediaItems.value.filter(item => item.id !== id);
};

const moveMediaItem = (id, direction) => {
  const idx = mediaItems.value.findIndex(item => item.id === id);
  if (idx === -1) return;
  const newIdx = idx + direction;
  if (newIdx < 0 || newIdx >= mediaItems.value.length) return;
  const items = [...mediaItems.value];
  [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
  mediaItems.value = items;
};

const handleMediaSelectedForItem = (event, itemId) => {
  const file = event.target.files[0];
  if (!file) return;

  const item = mediaItems.value.find(i => i.id === itemId);
  if (!item) return;

  item.file = file;
  item.mediaType = mediaTypeFromFile(file);

  // Generate local preview
  const reader = new FileReader();
  reader.onload = e => {
    item.previewUrl = e.target.result;
  };
  reader.readAsDataURL(file);

  // Upload via DirectUpload
  uploadMediaForItem(file, itemId);
};

const uploadMediaForItem = async (file, itemId) => {
  const item = mediaItems.value.find(i => i.id === itemId);
  if (!item) return;

  item.isUploading = true;
  try {
    const upload = new DirectUpload(
      file,
      `/rails/active_storage/direct_uploads`,
      {
        directUploadWillCreateBlobWithXHR: xhr => {
          xhr.setRequestHeader('api_access_token', currentUser.value.access_token);
        },
      }
    );

    upload.create((error, blob) => {
      if (error) {
        item.mediaUrl = item.previewUrl; // fallback to data URL
      } else {
        // Use proxy URL and encode filename (spaces break Evolution API)
        item.mediaUrl = `/rails/active_storage/blobs/proxy/${blob.signed_id}/${encodeURIComponent(blob.filename)}`;
      }
      item.isUploading = false;
    });
  } catch (e) {
    item.mediaUrl = item.previewUrl;
    item.isUploading = false;
  }
};

const resetMediaItems = () => {
  mediaItems.value = [];
};

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  resetMediaItems();
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const uploadedItems = mediaItems.value.filter(item => item.mediaUrl);
  return {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
    media_items: uploadedItems.map((item, index) => ({
      media_url: item.mediaUrl,
      media_type: item.mediaType,
      caption: item.caption,
      file_signed_id: item.mediaUrl,
      position: index,
    })),
    // Backward compat: legacy fields for single-media campaigns
    media_url: uploadedItems[0]?.mediaUrl || '',
    media_type: uploadedItems[0]?.mediaType || '',
    media_caption: uploadedItems[0]?.caption || '',
    send_delay: parseMMSS(state.sendDelay),
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

const updateStateFromCampaign = campaign => {
  if (!campaign) return;

  const {
    title,
    message,
    inbox: { id: inboxId },
    audience,
    trigger_rules: triggerRules,
  } = campaign;

  Object.assign(state, {
    title: title || '',
    message: message || '',
    inboxId: inboxId || null,
    scheduledAt: null,
    selectedAudience: audience?.map(a => a.id) || [],
    sendDelay: formatSecondsToMMSS(triggerRules?.send_delay),
  });

  // Load media items from campaign
  const items = triggerRules?.media_items || [];
  if (items.length > 0) {
    mediaItems.value = items.map((item, i) => ({
      id: Date.now() + i,
      file: null,
      previewUrl: item.media_url,
      mediaUrl: item.media_url || '',
      mediaType: item.media_type || '',
      caption: item.caption || '',
      isUploading: false,
    }));
  } else if (triggerRules?.media_url) {
    // Backward compat: single media
    mediaItems.value = [
      {
        id: Date.now(),
        file: null,
        previewUrl: triggerRules.media_url,
        mediaUrl: triggerRules.media_url || '',
        mediaType: triggerRules.media_type || '',
        caption: triggerRules.media_caption || '',
        isUploading: false,
      },
    ];
  }
};

watch(
  () => props.selectedCampaign,
  newCampaign => {
    if (props.mode === 'duplicate' && newCampaign) {
      updateStateFromCampaign(newCampaign);
    }
  },
  { immediate: true }
);
</script>

<template>
  <form class="flex flex-col gap-4 py-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.DISPARO.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MESSAGE.LABEL') }}
      </label>
      <Editor
        v-model="state.message"
        :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.MESSAGE.PLACEHOLDER')"
        channel-type="Channel::Api"
        :show-character-count="true"
        :max-length="4096"
        :message="formErrors.message"
        :message-type="formErrors.message ? 'error' : 'info'"
        enable-variables
        enable-canned-responses
      />
    </div>

    <!-- Media upload: multi-item with rich caption editors -->
    <div class="flex flex-col gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1">
      <div class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.LABEL') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ mediaItems.length }} / {{ MAX_MEDIA_ITEMS }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11 -mt-2">
        {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.SUPPORTED') }}
      </p>

      <!-- Media items list -->
      <div
        v-for="(item, index) in mediaItems"
        :key="item.id"
        class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-2"
      >
        <!-- Header: position + reorder + remove -->
        <div class="flex items-center justify-between">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.MEDIA_ITEM', { index: index + 1 }) }}
          </span>
          <div class="flex items-center gap-1">
            <button
              type="button"
              :title="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.MOVE_UP')"
              :disabled="index === 0"
              class="flex items-center justify-center w-6 h-6 rounded-md bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 disabled:opacity-30 disabled:cursor-not-allowed"
              @click="moveMediaItem(item.id, -1)"
            >
              <i class="i-lucide-chevron-up size-3" />
            </button>
            <button
              type="button"
              :title="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.MOVE_DOWN')"
              :disabled="index === mediaItems.length - 1"
              class="flex items-center justify-center w-6 h-6 rounded-md bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 disabled:opacity-30 disabled:cursor-not-allowed"
              @click="moveMediaItem(item.id, 1)"
            >
              <i class="i-lucide-chevron-down size-3" />
            </button>
            <button
              type="button"
              :title="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.REMOVE_MEDIA')"
              class="flex items-center justify-center w-6 h-6 rounded-full bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4"
              @click="removeMediaItem(item.id)"
            >
              <i class="i-lucide-x size-3" />
            </button>
          </div>
        </div>

        <!-- File picker (if no file yet) -->
        <div v-if="!item.file" class="flex items-center gap-3">
          <label
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer bg-n-alpha-3 text-n-blue-11 hover:bg-n-alpha-4"
          >
            <i class="i-lucide-paperclip size-4" />
            {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.CHOOSE_FILE') }}
            <input
              type="file"
              accept="image/*,video/*,audio/*"
              class="hidden"
              @change="handleMediaSelectedForItem($event, item.id)"
            />
          </label>
        </div>

        <!-- Preview + info (if file selected) -->
        <div v-if="item.file" class="flex flex-col gap-3">
          <div class="flex items-start gap-3">
            <!-- Image preview -->
            <div
              v-if="item.mediaType === 'image'"
              class="relative w-32 h-32 overflow-hidden rounded-lg bg-n-alpha-3"
            >
              <img
                :src="item.previewUrl"
                alt="Preview"
                class="object-cover w-full h-full"
              />
            </div>
            <!-- Video preview -->
            <video
              v-else-if="item.mediaType === 'video'"
              :src="item.previewUrl"
              controls
              class="w-48 h-32 rounded-lg bg-n-alpha-3"
            />
            <!-- Audio preview -->
            <audio
              v-else-if="item.mediaType === 'audio'"
              :src="item.previewUrl"
              controls
              class="w-48"
            />
            <!-- Document fallback -->
            <div
              v-else
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-alpha-3"
            >
              <i class="i-lucide-file size-4 text-n-slate-11" />
              <span class="text-sm text-n-slate-11">{{ item.file.name }}</span>
            </div>
          </div>

          <!-- Upload progress -->
          <div v-if="item.isUploading" class="flex items-center gap-2">
            <div
              class="w-4 h-4 border-2 rounded-full animate-spin border-n-blue-9 border-t-transparent"
            />
            <span class="text-xs text-n-slate-11">{{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.UPLOADING') }}</span>
          </div>

          <!-- URL input as fallback -->
          <Input
            v-model="item.mediaUrl"
            :label="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.URL_LABEL')"
            :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.URL_PLACEHOLDER')"
          />

          <!-- Rich caption editor per media item -->
          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-xs font-medium text-n-slate-12">
              {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.CAPTION_LABEL') }}
            </label>
            <Editor
              v-model="item.caption"
              :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.CAPTION_PLACEHOLDER')"
              channel-type="Channel::Api"
              :show-character-count="false"
              :enable-variables="false"
              :enable-canned-responses="false"
            />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div
        v-if="mediaItems.length === 0"
        class="flex flex-col items-center justify-center py-4 text-center"
      >
        <i class="i-lucide-image size-8 text-n-slate-8 mb-2" />
        <span class="text-sm text-n-slate-11">
          {{ t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.NO_MEDIA') }}
        </span>
      </div>

      <!-- Add media button -->
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAMPAIGN.DISPARO.CREATE.FORM.MEDIA.ADD_MEDIA')"
        :disabled="mediaItems.length >= MAX_MEDIA_ITEMS"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="addMediaItem"
      >
        <template #icon>
          <i class="i-lucide-plus size-4" />
        </template>
      </Button>
    </div>

    <!-- Inbox selector -->
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DISPARO.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Audience -->
    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DISPARO.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.DISPARO.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Send delay -->
    <div class="flex flex-col gap-1">
      <label for="sendDelay" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.DISPARO.CREATE.FORM.DELAY.LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11 mb-1">
        {{ t('CAMPAIGN.DISPARO.CREATE.FORM.DELAY.HELP') }}
      </p>
      <Input
        id="sendDelay"
        v-model="state.sendDelay"
        :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.DELAY.PLACEHOLDER')"
      />
    </div>

    <!-- Schedule -->
    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.DISPARO.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.DISPARO.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex items-center justify-between w-full gap-3 pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.DISPARO.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.DISPARO.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
